import React, {useReducer, useState} from 'react';
import {ContentAdDesktop} from 'app/styles/content';
import {getPointer} from 'app/helpers/getPointer';
import {formatDateWithWeekday} from 'app/helpers/dateHelpers';
import {MatchRow} from 'app/components/match/matchRow/MatchRow';
import {Helmet} from 'react-helmet-async';
import {object, string} from 'prop-types';
import {fetchClubMatches, overwriteMatches} from 'app/routes/club/ClubPageActions';
import {LoadingIndicator, LOADING_MESSAGE} from '@fupa/fupa-uikit';
import {StreamPlaceholder} from 'app/styles/stream';
import {useLocation, useParams} from 'react-router';
import {useFetchData, useRestoreData, useEffectAfterMount} from 'app/hooks/dataHooks';
import {ClubMatchesPageReducer} from 'app/routes/club/ClubPageReducer';
import queryString from 'query-string';
import {useInfiniteScrollComponentState} from 'app/hooks/useInfiniteScroll';
import {ButtonGroup} from 'app/components/button/ButtonGroup';
import {MatchdayCard} from 'app/components/matchCalendar/matchdayCard';
import {StickyHeader} from 'app/components/matchCalendar/stickyHeader';
import {MatchRowsCardWrapper} from 'app/components/match/matchRow/MatchRowStyles';
import CompetitionCategory from 'app/models/competition/CompetitionCategory';

const ClubMatchesPage = ({club, districtName, dispatchRedux}) => {
  const {restoredData} = useRestoreData('ClubMatchesPage');
  const {clubSlug} = useParams();
  const location = useLocation();
  const search = location.search;
  const searchQuery = queryString.parse(search);
  const initData = {
    isFetching: null,
    items: [],
    nextUrl: '',
    error: null,
  };
  const initialState = restoredData ?? initData;
  const [state, dispatch] = useReducer(ClubMatchesPageReducer, initialState);
  const {items, isFetching, error} = state;
  const [activeButton, setActiveButton] = useState(getPointer(searchQuery));

  const loadData = (reloading, reset) => {
    const componentState = reset ? initData : state;
    const pointerQuery = getPointer(searchQuery);
    return fetchClubMatches(clubSlug, pointerQuery, reloading, dispatchRedux)(dispatch, componentState);
  };

  useInfiniteScrollComponentState(loadData, items.length);
  useFetchData(state, loadData, 'ClubMatchesPage', true);

  const title = 'Spiele - ';
  const meta = [
    {
      name: 'description',
      content: `Vereinsspielplan ➤ Liveticker ➤ Spielberichte ➤ Ergebnisse ➤ Aufstellungen ➤ ${club.name} ➤ ${club.middleName} ➤ ${club.shortName} ➤ ${districtName}`,
    },
    {
      name: 'robots',
      content: 'index, follow',
    },
  ];

  const canonical = searchQuery?.pointer ? (
    <link rel='canonical' href={`https://www.fupa.net/club/${clubSlug}/matches`} />
  ) : null;

  useEffectAfterMount(() => {
    // Request data on switching matches by navigation with toggle buttons
    const pointer = getPointer(searchQuery);
    setActiveButton(pointer);
    dispatch(overwriteMatches(initData));
    loadData(null, true);
  }, [location]);

  let testMatchAgeGroup;
  const showLoadingMatchesNext = isFetching ? (
    <LoadingIndicator bgColor message={LOADING_MESSAGE.loadingMatches} />
  ) : error ? (
    <StreamPlaceholder>Beim Laden ist ein Fehler aufgetreten.</StreamPlaceholder>
  ) : null;

  const activeButtonText = activeButton === 'prev' ? 'vergangenen' : 'aktuellen';

  const matchRows = matches => {
    return (
      <MatchRowsCardWrapper>
        {matches.map(match => (
          <MatchRow standalone={true} key={match.slug} match={match} colored={true} ownClubSlug={clubSlug} />
        ))}
      </MatchRowsCardWrapper>
    );
  };

  const matchDayCard = competitionGroup => {
    if (competitionGroup?.matches?.length) {
      if (
        competitionGroup.competition &&
        new CompetitionCategory(competitionGroup.competition.category).isTestmatch()
      ) {
        const team =
          clubSlug === competitionGroup.matches[0].homeTeam.clubSlug
            ? competitionGroup.matches[0].homeTeam
            : competitionGroup.matches[0].awayTeam;
        testMatchAgeGroup = `- ${team.ageGroup.name}`;
      } else {
        testMatchAgeGroup = '';
      }
      return (
        <MatchdayCard
          key={competitionGroup.competition.competitionSeasonId}
          competitionGroup={competitionGroup}
          extendWithAgeGroup={testMatchAgeGroup}
          footer={false}>
          {matchRows(competitionGroup.matches)}
        </MatchdayCard>
      );
    } else {
      return <StreamPlaceholder>Keine {activeButtonText} Spiele verfügbar.</StreamPlaceholder>;
    }
  };

  const matchDays = items.map((kickoffGroup, index) => {
    if (kickoffGroup.day) {
      return (
        <div key={kickoffGroup.day} id={kickoffGroup.day}>
          <StickyHeader title={formatDateWithWeekday(kickoffGroup.day)} />
          {kickoffGroup.competitionGroup.map(group => matchDayCard(group))}
        </div>
      );
    } else {
      return (
        <div key={index} id={index}>
          {kickoffGroup?.competitionGroup.map(group => matchDayCard(group))}
        </div>
      );
    }
  });

  const buttons = [
    {
      url: `/club/${clubSlug}/matches`,
      label: 'Aktuelle Spiele',
      value: 'current',
    },
    {
      url: `/club/${clubSlug}/matches?pointer=prev`,
      label: 'Vergangene Spiele',
      value: 'prev',
    },
  ];
  return (
    <ContentAdDesktop bgColor top='7.125rem' superbannerOnly>
      <ButtonGroup activeValue={activeButton} onChange={value => setActiveButton(value)} buttons={buttons} />
      {matchDays}
      {showLoadingMatchesNext}
      <Helmet title={title} meta={meta}>
        {canonical}
      </Helmet>
    </ContentAdDesktop>
  );
};

ClubMatchesPage.propTypes = {
  match: object.isRequired,
  defaultTitle: string.isRequired,
  districtName: string.isRequired,
};

export default ClubMatchesPage;
